// frontend/src/components/reports/filterPredicates.ts
//
// Traduce el estado de filtros a predicados de bloque. Vive aparte de los
// componentes y sin dependencias de React para poder cubrirlo con vitest.
//
// El nombre no es `reportFilters` a propósito: chocaría con `ReportFilters.tsx`
// en sistemas de ficheros que no distinguen mayúsculas, y TypeScript trataría
// los dos módulos como uno.
//
// Todo el estado son strings porque se sincroniza con la query string: así un
// report filtrado es enlazable y sobrevive a un refresco.
import type { RowPredicate } from '@/utils/reportBlocks'
import type { ReportDef, ReportFilterSpec } from './reportDefs'

export type ReportFiltersState = Record<string, string>

/** Separador de los filtros multi-valor dentro de su string de estado.
 *
 *  El estado entero son strings porque viaja en la query string, así que los
 *  varios términos de un mismo filtro se guardan unidos. Se eligió la barra
 *  vertical por ser legible en la URL y no aparecer en códigos de artículo,
 *  facturas ni pedidos; si alguien la teclea dentro de un término, lo parte en
 *  dos, que en una búsqueda OR es inocuo. */
export const SEPARADOR_MULTI = '|'

/** Los valores de un filtro multi-valor. */
export function valoresMulti(valor: string | undefined): string[] {
  return (valor ?? '').split(SEPARADOR_MULTI).map(v => v.trim()).filter(Boolean)
}

/** El string de estado de una selección. Vacío = filtro sin valor. */
export function estadoMulti(valores: string[]): string {
  return valores.join(SEPARADOR_MULTI)
}

export const EMPTY_FILTERS: ReportFiltersState = {}

/** Claves de estado que ocupa un filtro. `dateWindow` ocupa dos. */
export function filterStateKeys(spec: ReportFilterSpec): string[] {
  return spec.kind === 'dateWindow'
    ? [`${spec.key}Desde`, `${spec.key}Hasta`]
    : [spec.key]
}

/** Cuántos filtros tienen valor, para el contador del encabezado. */
export function contarActivos(def: ReportDef, filters: ReportFiltersState): number {
  let n = 0
  for (const spec of def.filters) {
    if (filterStateKeys(spec).some(k => (filters[k] ?? '') !== '')) n++
  }
  return n
}

function readField(row: unknown, field: string): unknown {
  return (row as Record<string, unknown>)[field]
}

/** Valores completos que existen en el dataset, por filtro `multiText`. */
export type CatalogosMultiText = Record<string, Set<string>>

/**
 * Qué términos de un `multiText` son un valor COMPLETO del campo.
 *
 * Existe por una colisión real: en consumo de producción conviven
 * `ARF32120414` y `XARF32120414` —dos artículos distintos, uno el estándar y
 * otro su variante—, y una búsqueda por subcadena hacía que filtrar por el
 * primero trajera también el segundo, porque el código del uno está dentro del
 * otro. Lo mismo pasaría con dos facturas `F-100` y `F-1000`.
 *
 * La regla que sale de aquí es la que espera cualquiera: **un valor completo casa
 * exacto, un fragmento casa por subcadena**. Sin esto no hay forma de pedir un
 * artículo y sólo ese, ni desde el desplegable ni pegando la referencia entera.
 */
export function catalogosMultiText<T>(
  def: ReportDef,
  rows: T[],
): CatalogosMultiText {
  const out: CatalogosMultiText = {}
  for (const spec of def.filters) {
    if (spec.kind !== 'multiText' || !spec.field) continue
    const field = spec.field
    const valores = new Set<string>()
    for (const row of rows) {
      const v = readField(row, field)
      if (v != null && v !== '') valores.add(String(v).trim().toLowerCase())
    }
    out[spec.key] = valores
  }
  return out
}

/**
 * Predicado de un filtro, o `null` si no tiene valor.
 *
 * `dateWindow` se interpreta como **solape**: el bloque entra si su ventana de
 * recepción toca el rango pedido. Exigir que la ventana entera quepa dentro
 * dejaría fuera un pedido cuyas recepciones empezaron antes y siguen abiertas,
 * que es justo el que interesa mirar. Las fechas se comparan como texto porque
 * llegan en ISO y ahí el orden lexicográfico es el cronológico.
 */
function buildPredicate<T>(
  spec: ReportFilterSpec,
  filters: ReportFiltersState,
  catalogos?: CatalogosMultiText,
): RowPredicate<T> {
  if (spec.kind === 'dateWindow') {
    const desde = filters[`${spec.key}Desde`] ?? ''
    const hasta = filters[`${spec.key}Hasta`] ?? ''
    if (!desde && !hasta) return null
    const from = spec.fieldFrom!
    const to = spec.fieldTo!
    return row => {
      const rowFrom = readField(row, from)
      const rowTo = readField(row, to)
      // Sin fechas de recepción no hay solape posible: el bloque no entra.
      if (rowFrom == null && rowTo == null) return false
      const lo = String(rowFrom ?? rowTo)
      const hi = String(rowTo ?? rowFrom)
      if (desde && hi < desde) return false
      if (hasta && lo > hasta) return false
      return true
    }
  }

  const value = filters[spec.key] ?? ''
  if (value === '') return null
  const field = spec.field!

  if (spec.kind === 'bool') {
    const want = value === '1' ? 1 : 0
    return row => Number(readField(row, field) ?? 0) === want
  }

  if (spec.kind === 'select') {
    return row => String(readField(row, field) ?? '') === value
  }

  // Multi-valor: OR entre los términos del mismo filtro. Con uno solo cae en el
  // mismo predicado que su versión simple, así que migrar no cambia resultados.
  if (spec.kind === 'multiSelect') {
    const elegidos = new Set(valoresMulti(value))
    if (elegidos.size === 0) return null
    return row => elegidos.has(String(readField(row, field) ?? ''))
  }

  if (spec.kind === 'multiText') {
    const agujas = valoresMulti(value).map(v => v.toLowerCase())
    if (agujas.length === 0) return null
    // Un término que ES un valor completo del campo casa por IGUALDAD; uno que no
    // lo es, por subcadena. Sin el catálogo se comporta como siempre —todo por
    // subcadena—, que es lo que hace que los tests sin dataset sigan valiendo.
    const completos = catalogos?.[spec.key]
    const exactas = completos ? agujas.filter(a => completos.has(a)) : []
    const parciales = agujas.filter(a => !exactas.includes(a))
    return row => {
      const texto = String(readField(row, field) ?? '').trim().toLowerCase()
      if (exactas.includes(texto)) return true
      return parciales.some(a => texto.includes(a))
    }
  }

  // text: subcadena sin distinguir mayúsculas, que es lo que espera quien pega
  // un código a medias.
  const needle = value.trim().toLowerCase()
  return row => String(readField(row, field) ?? '').toLowerCase().includes(needle)
}

/**
 * Predicados separados por nivel, listos para `filterBlocks`.
 *
 * La separación no es cosmética: los de cabecera descartan el bloque entero, y
 * los de detalle lo conservan completo si alguna sublínea casa. De ahí que
 * buscar un artículo nunca deje sublíneas huérfanas.
 */
export function buildFilterPredicates<T>(
  def: ReportDef,
  filters: ReportFiltersState,
  catalogos?: CatalogosMultiText,
): { header: RowPredicate<T>[]; detail: RowPredicate<T>[] } {
  const header: RowPredicate<T>[] = []
  const detail: RowPredicate<T>[] = []

  for (const spec of def.filters) {
    const p = buildPredicate<T>(spec, filters, catalogos)
    if (!p) continue
    if (spec.scope === 'header') header.push(p)
    else detail.push(p)
  }

  return { header, detail }
}

/**
 * ¿Es ESTA sublínea la que ha traído aquí a su bloque?
 *
 * Devuelve `null` cuando no hay ningún filtro de detalle activo, y entonces la
 * tabla se comporta como siempre: todo plegado y sin resaltar. Con filtros
 * activos, casa la sublínea que cumple TODOS —el mismo criterio con el que
 * `filterBlocks` decide que el bloque entra—, de modo que lo resaltado es
 * exactamente lo que explica por qué está el bloque en la lista.
 *
 * Sólo mira los filtros de DETALLE, y no es un olvido. Un filtro de cabecera ya
 * describe al bloque entero: en materia prima, buscar un artículo trae el pedido
 * porque su cabecera casa, y sus lotes casarían todos —el código de artículo se
 * repite en cada uno—. Resaltarlos sería pintar de azul el bloque completo, que
 * no distingue nada de nada.
 */
export function buildDetailMatcher<T>(
  def: ReportDef,
  filters: ReportFiltersState,
  catalogos?: CatalogosMultiText,
): ((row: T) => boolean) | null {
  const activos = buildFilterPredicates<T>(def, filters, catalogos).detail
    .filter(Boolean) as ((row: T) => boolean)[]
  if (activos.length === 0) return null
  return row => activos.every(p => p(row))
}
